if (!customElements.get('quiz-section')) {
  customElements.define(
    'quiz-section',
    class QuizSection extends HTMLElement {
      connectedCallback() {
        this._key = `wt-quiz-${this.dataset.sectionId}`;
        this.slides = Array.from(this.querySelectorAll('quiz-slide'));

        this.slides.forEach(slide => {
          const type = this._getSlideType(slide);
          if (type) slide.classList.add(`wt-quiz__slide--${type}`);
        });

        const questions = this.slides.filter(slide =>
          this._isQuestionSlide(slide)
        );

        if (questions.length) {
          questions.at(-1).classList.add('wt-quiz__slide--last-question');
        }

        this._progressFill = this.querySelector('.wt-quiz__progress-fill');
        this._progressText = this.querySelector('.wt-quiz__progress-text');

        this._state = {
          currentIndex: 0,
          tagMap: {},
          resultHandle: null,
          extrasHandle: null
        };

        try {
          const saved = JSON.parse(localStorage.getItem(this._key));

          if (saved?.completedAt) {
            localStorage.removeItem(this._key);
          }
        } catch (_) {}

        document.body.classList.remove('lamav-quiz-results-active');

        this._activate(0, false);

        this._onAnswer = this._handleAnswer.bind(this);
        this._onNewsletterSubmit = this._handleNewsletterSubmit.bind(this);
        this._onClick = this._handleClick.bind(this);

        this.addEventListener('quiz:answer', this._onAnswer);
        this.addEventListener('click', this._onClick);

        this._newsletterForm = this.querySelector(
          '.wt-quiz__newsletter-form'
        );

        this._newsletterForm?.addEventListener(
          'submit',
          this._onNewsletterSubmit
        );
      }

      disconnectedCallback() {
        this.removeEventListener('quiz:answer', this._onAnswer);
        this.removeEventListener('click', this._onClick);

        this._newsletterForm?.removeEventListener(
          'submit',
          this._onNewsletterSubmit
        );

        document.body.classList.remove('lamav-quiz-results-active');
      }

      _handleClick(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const nextButton = target.closest(
          '.wt-quiz__nav-next:not([disabled])'
        );

        if (nextButton) {
          if (nextButton.dataset.redirectUrl) {
            window.location.href = nextButton.dataset.redirectUrl;
            return;
          }

          this._advance();
          return;
        }

        if (target.closest('.wt-quiz__nav-prev')) {
          this._back();
          return;
        }

        if (target.closest('.wt-quiz__retake')) {
          event.preventDefault();
          this._retake();
          return;
        }

        if (target.closest('.wt-quiz__newsletter-skip')) {
          event.preventDefault();
          this._advance();
          return;
        }

        const newsletterButton = target.closest('.wt-newsletter__send');

        if (newsletterButton) {
          event.preventDefault();
          newsletterButton.closest('form')?.requestSubmit();
          return;
        }

        const introButton = target.closest('.hero__button');
        const slide = introButton?.closest('quiz-slide');

        if (introButton && this._getSlideType(slide) === 'intro') {
          event.preventDefault();
          this._advance();
        }
      }

      _activate(index, animate = true) {
        const activeSlide = this.slides[index];
        if (!activeSlide) return;

        const activeType = this._getSlideType(activeSlide);

        document.body.classList.toggle(
          'lamav-quiz-results-active',
          activeType === 'results'
        );

        this.slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === index);
        });

        this._state.currentIndex = index;

        if (activeType === 'results') {
          this._renderApprovedProfile();
        }

        this._syncProgress(index);
        this._syncNavButtons(index);

        if (animate) {
          const firstOption = activeSlide.querySelector('quiz-option');

          if (firstOption) {
            requestAnimationFrame(() => {
              firstOption.focus({ preventScroll: true });
            });
          }
        }
      }

      _syncNavButtons(index) {
        const slide = this.slides[index];
        if (!slide) return;

        const nextButton = slide.querySelector('.wt-quiz__nav-next');
        if (!nextButton) return;

        const questions = this.slides.filter(item =>
          this._isQuestionSlide(item)
        );

        const questionIndex = questions.indexOf(slide);
        if (questionIndex === -1) return;

        const hasSelection = !!slide.querySelector(
          'quiz-option.is-selected'
        );

        nextButton.disabled = !hasSelection;
        delete nextButton.dataset.redirectUrl;

        const previousButton = slide.querySelector('.wt-quiz__nav-prev');

        if (previousButton) {
          previousButton.hidden = questionIndex === 0;
        }

        const isLast = questionIndex === questions.length - 1;
        const label = nextButton.querySelector('.wt-quiz__nav-label');

        if (label) {
          label.textContent = isLast
            ? nextButton.dataset.resultLabel || label.textContent
            : nextButton.dataset.nextLabel || label.textContent;
        }

        if (isLast && hasSelection) {
          const url = this._getRedirectUrl();

          if (url) {
            nextButton.dataset.redirectUrl = url;
          }
        }
      }

      _syncProgress(index) {
        const questions = this.slides.filter(slide =>
          this._isQuestionSlide(slide)
        );

        const current = this.slides[index];
        const isQuestion = this._isQuestionSlide(current);
        const progress = this.querySelector('.wt-quiz__progress');

        if (progress) progress.hidden = !isQuestion;
        if (!isQuestion) return;

        const header = this.querySelector('.wt-quiz__header');

        if (header) {
          const styles = getComputedStyle(current);

          header.style.backgroundColor = styles
            .getPropertyValue('--quiz-block-bg')
            .trim();

          header.style.color = styles
            .getPropertyValue('--quiz-block-text')
            .trim();
        }

        const questionIndex = questions.indexOf(current) + 1;
        const total = questions.length;

        if (this._progressFill) {
          this._progressFill.style.width =
            `${(questionIndex / total) * 100}%`;
        }

        if (this._progressText) {
          this._progressText.textContent = `${questionIndex} / ${total}`;
        }
      }

      _handleAnswer(event) {
        const parseTags = value =>
          (value || '')
            .split(',')
            .map(tag => tag.trim().toLowerCase())
            .filter(Boolean);

        parseTags(event.detail.oldTags).forEach(tag => {
          if (this._state.tagMap[tag] > 1) {
            this._state.tagMap[tag]--;
          } else {
            delete this._state.tagMap[tag];
          }
        });

        parseTags(event.detail.tags).forEach(tag => {
          this._state.tagMap[tag] =
            (this._state.tagMap[tag] || 0) + 1;
        });

        this._save();
        this._syncNavButtons(this._state.currentIndex);
      }

      _hasTag(tag) {
        return (this._state.tagMap[tag.toLowerCase()] || 0) > 0;
      }

      _getTagByPrefix(prefix) {
        return (
          Object.keys(this._state.tagMap || {}).find(
            tag =>
              tag.startsWith(prefix) &&
              this._state.tagMap[tag] > 0
          ) || null
        );
      }

      _getAnswers() {
        return {
          concern: this._getTagByPrefix('concern-'),
          skin: this._getTagByPrefix('skin-'),
          routine: this._getTagByPrefix('routine-'),
          age: this._getTagByPrefix('age-'),
          pregnancy: this._getTagByPrefix('pregnancy-')
        };
      }

      _determinePrimaryResult() {
        const { concern, skin, routine, age } = this._getAnswers();

        if (age === 'age-16-20') {
          return 'primary-teen';
        }

        if (age === 'age-20-30') {
          if (concern === 'concern-fine-lines') {
            return 'primary-20-30-firming';
          }

          if (concern === 'concern-dullness') {
            return 'primary-20-30-brightening';
          }

          if (concern === 'concern-dehydration') {
            return 'primary-20-30-hydration';
          }

          if (concern === 'concern-breakouts') {
            return 'primary-20-30-breakouts';
          }
        }

        if (age === 'age-30-45') {
          if (concern === 'concern-fine-lines') {
            if (routine === 'routine-simple') {
              return 'primary-30-45-firming-simple';
            }

            if (routine === 'routine-targeted') {
              if (skin === 'skin-dry') {
                return 'primary-30-45-firming-targeted-dry';
              }

              if (
                skin === 'skin-oily' ||
                skin === 'skin-combination'
              ) {
                return 'primary-30-45-firming-targeted-oily';
              }

              if (skin === 'skin-balanced') {
                return 'primary-30-45-firming-targeted-balanced';
              }
            }
          }

          if (concern === 'concern-dullness') {
            if (routine === 'routine-simple') {
              return 'primary-30-45-brightening-simple';
            }

            if (routine === 'routine-targeted') {
              return 'primary-30-45-brightening-targeted';
            }
          }

          if (concern === 'concern-dehydration') {
            if (routine === 'routine-simple') {
              return 'primary-30-45-hydration-simple';
            }

            if (routine === 'routine-targeted') {
              return 'primary-30-45-hydration-targeted';
            }
          }

          if (concern === 'concern-breakouts') {
            return 'primary-30-45-breakouts';
          }
        }

        if (age === 'age-45-plus') {
          return routine === 'routine-targeted'
            ? 'primary-45-targeted'
            : 'primary-45-simple';
        }

        return null;
      }

      _determineExtrasResult() {
        const { concern, age, pregnancy } = this._getAnswers();

        if (pregnancy === 'pregnancy-yes') {
          return 'extras-pregnancy';
        }

        if (age === 'age-16-20') {
          return 'extras-teen';
        }

        if (age === 'age-20-30') {
          return 'extras-20-30';
        }

        if (age === 'age-30-45') {
          if (concern === 'concern-fine-lines') {
            return 'extras-30-45-firming';
          }

          if (concern === 'concern-dullness') {
            return 'extras-30-45-brightening';
          }

          if (concern === 'concern-dehydration') {
            return 'extras-30-45-hydration';
          }

          if (concern === 'concern-breakouts') {
            return 'extras-30-45-breakouts';
          }
        }

        if (age === 'age-45-plus') {
          return 'extras-45';
        }

        return null;
      }

      _handleNewsletterSubmit(event) {
        event.preventDefault();

        const form = event.target;

        fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          redirect: 'manual'
        })
          .catch(() => {})
          .finally(() => this._advance());
      }

      _advance() {
        const next = this._state.currentIndex + 1;

        if (next >= this.slides.length) return;

        if (next === this._resultsIndex()) {
          this._calcResult();
          this._state.completedAt = new Date().toISOString();
          this._save();
        }

        this._activate(next);
      }

      _back() {
        const previous = this._state.currentIndex - 1;

        if (previous >= 0) {
          this._activate(previous);
        }
      }

      _retake() {
        const firstQuestion = this.slides.findIndex(slide =>
          this._isQuestionSlide(slide)
        );

        const startIndex = firstQuestion >= 0 ? firstQuestion : 0;

        this._state = {
          currentIndex: startIndex,
          tagMap: {},
          resultHandle: null,
          extrasHandle: null
        };

        try {
          localStorage.removeItem(this._key);
        } catch (_) {}

        this.querySelectorAll('quiz-option').forEach(option => {
          option.classList.remove('is-selected');
        });

        this._clearResults();

        if (this._progressFill) {
          this._progressFill.style.transition = 'none';
          this._progressFill.style.width = '0%';

          requestAnimationFrame(() => {
            this._progressFill.style.transition = '';
          });
        }

        document.body.classList.remove('lamav-quiz-results-active');
        this._activate(startIndex);
      }

      _getSlideType(slide) {
        return (
          slide?.querySelector('[data-block-type]')?.dataset.blockType ||
          null
        );
      }

      _isQuestionSlide(slide) {
        return this._getSlideType(slide) === 'question';
      }

      _resultsIndex() {
        return this.slides.findIndex(
          slide => this._getSlideType(slide) === 'results'
        );
      }

      _getRedirectUrl() {
        const results = this.querySelector('.wt-quiz__results');

        if (results?.dataset.redirect !== 'true') return null;

        const primaryTag = this._determinePrimaryResult();
        if (!primaryTag) return null;

        const match = Array.from(
          this.querySelectorAll(
            '.wt-quiz__result-items [data-result-tag]'
          )
        ).find(item => item.dataset.resultTag === primaryTag);

        return match?.dataset.url || null;
      }

      _clearResults() {
        [
          '.lamav-quiz-primary__content',
          '.lamav-quiz-extras__products',
          '.wt-quiz__result-info',
          '.wt-quiz__extras-info'
        ].forEach(selector => {
          const element = this.querySelector(selector);

          if (element) {
            element.replaceChildren();

            if (
              selector === '.wt-quiz__result-info' ||
              selector === '.wt-quiz__extras-info'
            ) {
              element.hidden = true;
            }
          }
        });

        [
          '.lamav-quiz-primary',
          '.lamav-quiz-extras',
          '.wt-quiz__result-fallback'
        ].forEach(selector => {
          const element = this.querySelector(selector);
          if (element) element.hidden = true;
        });
      }

      _calcResult() {
        this._state.resultHandle = this._determinePrimaryResult();
        this._state.extrasHandle = this._determineExtrasResult();

        this._renderSavedResult();
        this._save();
      }

      _renderSavedResult() {
        const items = Array.from(
          this.querySelectorAll(
            '.wt-quiz__result-items [data-result-tag]'
          )
        );

        const primary = items.find(
          item => item.dataset.resultTag === this._state.resultHandle
        );

        const extras = items.find(
          item => item.dataset.resultTag === this._state.extrasHandle
        );

        this._clearResults();

        const renderResult = (
          original,
          infoSelector,
          contentSelector,
          zoneSelector
        ) => {
          if (!original) return;

          const sourceInfo = original.querySelector(
            '.wt-quiz__result-item-info'
          );

          const info = this.querySelector(infoSelector);
          const content = this.querySelector(contentSelector);
          const zone = this.querySelector(zoneSelector);

          if (sourceInfo && info) {
            info.innerHTML = sourceInfo.innerHTML;
            info.hidden = false;
          }

          if (content) {
            const clone = original.cloneNode(true);
            clone.hidden = false;
            content.appendChild(clone);
            this._refreshSlider(clone);
          }

          if (zone) zone.hidden = false;
        };

        renderResult(
          primary,
          '.wt-quiz__result-info',
          '.lamav-quiz-primary__content',
          '.lamav-quiz-primary'
        );

        renderResult(
          extras,
          '.wt-quiz__extras-info',
          '.lamav-quiz-extras__products',
          '.lamav-quiz-extras'
        );

        if (!primary) this._state.resultHandle = null;
        if (!extras) this._state.extrasHandle = null;

        const fallback = this.querySelector('.wt-quiz__result-fallback');

        if (fallback) fallback.hidden = !!primary;

        this._renderApprovedProfile();
      }

      _renderApprovedProfile() {
        const info = this.querySelector('.wt-quiz__result-info');

        if (!info || !this._state.resultHandle) return;

        const { skin, concern, routine } = this._getAnswers();

        const skinProfiles = {
          'skin-balanced': {
            title: 'Balanced Skin',
            text: "Your skin generally feels comfortable, without being particularly oily or dry. Your routine focuses on maintaining that balance, keeping your skin hydrated and addressing the concern you'd like support with."
          },
          'skin-dry': {
            title: 'Dry Skin',
            text: "Your skin tends to feel dry or tight and may need extra nourishment to feel comfortable. Your routine focuses on replenishing moisture and leaving your skin feeling softer and cared for."
          },
          'skin-oily': {
            title: 'Oily Skin',
            text: "Your skin tends to develop excess oil or shine. Your routine focuses on gentle cleansing and lightweight hydration, helping your skin feel fresh and comfortable while addressing your main concern."
          },
          'skin-combination': {
            title: 'Combination Skin',
            text: "Your skin has a mix of needs, with some areas feeling oilier and others feeling normal or dry. Your routine focuses on balancing care across these areas, with hydration and targeted support where you need it."
          }
        };

        const concernProfiles = {
          'concern-fine-lines': {
            badge: 'Fine Lines & Ageing',
            eyebrow: 'Matched to fine lines & ageing concerns',
            title: 'Your Age Defence Routine',
            text: "You've selected visible signs of ageing as your main concern. Your recommended routine focuses on nourishing hydration and supporting smoother, firmer-looking skin, helping your complexion feel replenished and look refreshed."
          },
          'concern-dullness': {
            badge: 'Dullness & Uneven Skin Tone',
            eyebrow: 'Matched to dullness & uneven skin tone',
            title: 'Your Brightening Routine',
            text: "You've selected dullness or uneven skin tone as your main concern. Your recommended routine focuses on restoring the appearance of radiance and supporting a more even-looking complexion, helping your skin look fresh and luminous."
          },
          'concern-dehydration': {
            badge: 'Dryness & Dehydration',
            eyebrow: 'Matched to dryness & dehydration',
            title: 'Your Hydration Routine',
            text: "You've selected dryness or dehydration as your main concern. Your recommended routine focuses on replenishing moisture and nourishing your skin, helping it feel softer, more comfortable and hydrated."
          },
          'concern-breakouts': {
            badge: 'Excess Oil & Congestion',
            eyebrow: 'Matched to excess oil & congestion',
            title: 'Your Purify & Clean Routine',
            text: "You've selected excess oil or congestion as your main concern. Your recommended routine focuses on cleansing away impurities and supporting a balanced, fresh-feeling complexion, while keeping your skin's need for hydration in mind."
          }
        };

        const routineLabels = {
          'routine-simple': 'Simple Routine',
          'routine-targeted': 'Targeted Routine'
        };

        const profile = skinProfiles[skin];
        let focus = concernProfiles[concern];

        if (
          !focus &&
          /calm|restore/.test(this._state.resultHandle)
        ) {
          focus = {
            eyebrow: 'Gentle care & skin comfort',
            title: 'Your Calm & Restore Routine',
            text: "Your recommended routine focuses on gentle care, nourishment and hydration, helping your skin feel comfortable and cared for."
          };
        }

        if (!profile || !focus) return;

        const fragment = document.createDocumentFragment();

        const addText = (parent, tag, className, text) => {
          const element = document.createElement(tag);
          element.className = className;
          element.textContent = text;
          parent.appendChild(element);
          return element;
        };

        addText(
          fragment,
          'h2',
          'lamav-approved-profile__heading',
          'Your Skin Profile'
        );

        const badgeList = document.createElement('div');
        badgeList.className = 'wt-quiz__winning-tags';

        [
          profile.title,
          concernProfiles[concern]?.badge,
          routineLabels[routine]
        ]
          .filter(Boolean)
          .forEach(badge => {
            addText(
              badgeList,
              'span',
              'wt-quiz__winning-tag',
              badge
            );
          });

        fragment.appendChild(badgeList);

        addText(
          fragment,
          'h3',
          'lamav-approved-profile__type',
          profile.title
        );

        addText(
          fragment,
          'p',
          'lamav-approved-profile__description',
          profile.text
        );

        addText(
          fragment,
          'p',
          'lamav-approved-profile__intro',
          'Your recommended routine brings together your skin type, main concern and preferred number of steps. Introduce new products gradually and build a consistent routine that feels manageable for you.'
        );

        const routineSection = document.createElement('div');
        routineSection.className = 'lamav-approved-routine';

        addText(
          routineSection,
          'h2',
          'lamav-approved-routine__heading',
          'Your Personalised Routine'
        );

        addText(
          routineSection,
          'p',
          'lamav-approved-routine__eyebrow',
          focus.eyebrow
        );

        addText(
          routineSection,
          'h3',
          'lamav-approved-routine__title',
          focus.title
        );

        addText(
          routineSection,
          'p',
          'lamav-approved-routine__description',
          focus.text
        );

        fragment.appendChild(routineSection);

                const profilePanel = document.createElement('div');
        profilePanel.className = 'lamav-approved-profile-panel';

        [
          '.lamav-approved-profile__heading',
          '.lamav-approved-profile__type',
          '.lamav-approved-profile__description',
          '.wt-quiz__winning-tags'
        ].forEach(selector => {
          const element = fragment.querySelector(selector);

          if (element) {
            profilePanel.appendChild(element);
          }
        });

        fragment.prepend(profilePanel);
        info.replaceChildren(fragment);
        info.hidden = false;
      }

      _refreshSlider(container) {
        const slideshow = container?.querySelector('slideshow-section');
        if (!slideshow) return;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!slideshow.isConnected) return;

            if (slideshow.swiper) {
              slideshow.swiper.destroy(true, true);
              slideshow.swiper = null;
            }

            if (typeof slideshow.swiperInitilize === 'function') {
              slideshow.swiperInitilize();
            }
          });
        });
      }

      _save() {
        try {
          localStorage.setItem(
            this._key,
            JSON.stringify(this._state)
          );
        } catch (_) {}
      }
    }
  );
}

if (!customElements.get('quiz-slide')) {
  customElements.define(
    'quiz-slide',
    class QuizSlide extends HTMLElement {}
  );
}

if (!customElements.get('quiz-option')) {
  customElements.define(
    'quiz-option',
    class QuizOption extends HTMLElement {
      connectedCallback() {
        if (!this.hasAttribute('tabindex')) {
          this.setAttribute('tabindex', '0');
        }

        this._onClick = this._handleClick.bind(this);

        this._onKeydown = event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this._handleClick();
          }
        };

        this.addEventListener('click', this._onClick);
        this.addEventListener('keydown', this._onKeydown);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        this.removeEventListener('keydown', this._onKeydown);
      }

      _handleClick() {
        if (this.classList.contains('is-selected')) return;

        const options = this.closest('.wt-quiz__options');
        let oldTags = '';

        if (options) {
          const previous = options.querySelector(
            'quiz-option.is-selected'
          );

          if (previous) {
            oldTags = previous.dataset.tags || '';
          }

          options.querySelectorAll('quiz-option').forEach(option => {
            option.classList.remove('is-selected');
          });
        }

        this.classList.add('is-selected');

        this.dispatchEvent(
          new CustomEvent('quiz:answer', {
            bubbles: true,
            detail: {
              tags: this.dataset.tags || '',
              oldTags
            }
          })
        );
      }
    }
  );
}